import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Wordmark, LangToggle, Spinner } from "../components/common";
import { GoogleButton } from "../components/GoogleButton";
import { api, apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useRegion } from "../context/RegionContext";

export function AuthPage({ mode }) {
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isReg = mode === "register";
  const { region } = useRegion();
  const [form, setForm] = useState({ name: "", email: "", password: "", birth_date: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const from = location.state?.from && !["/login", "/registro"].includes(location.state.from) ? location.state.from : "/perfil";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isReg && (!form.name.trim() || !form.birth_date)) return setError(t("err.required"));
    if (isReg && !terms) return setError(t("legal.mustAccept"));
    setBusy(true);
    try {
      isReg ? await register({ ...form, name: form.name.trim(), accepted_terms: true }) : await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(apiError(err, t));
    }
    setBusy(false);
  };

  return (
    <div className="relative min-h-[100dvh] bg-mist" data-testid={isReg ? "register-page" : "login-page"}>
      <div className="relative h-48 overflow-hidden">
        <img src={region.hero_image} alt="" className="absolute inset-0 h-full w-full object-cover object-[center_60%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/0" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4 pt-safe-3">
          <button onClick={() => navigate(-1)} className="rounded-full bg-white/90 p-2 text-navy shadow" aria-label="back" data-testid="auth-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <LangToggle />
        </div>
        <div className="absolute inset-x-0 top-14 scale-[0.6]"><Wordmark size="lg" /></div>
      </div>
      <div className="relative -mt-8 mx-auto max-w-md rounded-t-[28px] bg-white px-6 pb-safe-6 pt-7 sm:mb-10 sm:rounded-[28px] sm:shadow-xl">
        <h1 className="text-2xl font-extrabold text-navy">{isReg ? t("auth.join") : t("auth.welcome")}</h1>
        {isReg && <p className="mt-1 text-sm text-slate-500">{t("auth.joinSub")}</p>}
        <form onSubmit={submit} className="mt-5 space-y-3.5">
          {isReg && <input className="field" placeholder={t("auth.name")} value={form.name} onChange={set("name")} autoComplete="name" data-testid="auth-name-input" />}
          <input className="field" type="email" placeholder={t("auth.email")} value={form.email} onChange={set("email")} autoComplete="email" required data-testid="auth-email-input" />
          <div>
            <input className="field" type="password" placeholder={t("auth.password")} value={form.password} onChange={set("password")}
              autoComplete={isReg ? "new-password" : "current-password"} minLength={isReg ? 6 : undefined} required data-testid="auth-password-input" />
            {isReg && <p className="mt-1 pl-1 text-xs text-slate-400">{t("auth.passwordHint")}</p>}
          </div>
          {isReg && (
            <label className="block">
              <span className="mb-1 block pl-1 text-xs font-bold uppercase tracking-wider text-navy/70">{t("auth.birthDate")}</span>
              <input className="field" type="date" value={form.birth_date} max={new Date().toISOString().slice(0, 10)} onChange={set("birth_date")} required data-testid="auth-birthdate-input" />
            </label>
          )}
          {isReg && (
            <label className="flex cursor-pointer items-start gap-2.5 pl-1 text-sm leading-snug text-slate-600">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#1E88E5]" data-testid="auth-terms-checkbox" />
              <span>
                {t("legal.accept1")}<Link to="/legal/condiciones" className="font-bold text-brand" data-testid="auth-terms-link">{t("legal.condiciones")}</Link>
                {t("legal.accept2")}<Link to="/legal/privacidad" className="font-bold text-brand" data-testid="auth-privacy-link">{t("legal.privacyPolicy")}</Link>
              </span>
            </label>
          )}
          {error && <p className="rounded-xl bg-alert/10 px-3 py-2 text-sm font-semibold text-alert" data-testid="auth-error">{error}</p>}
          {!isReg && (
            <Link to="/recuperar" className="block text-right text-sm font-semibold text-brand" data-testid="auth-forgot-link">{t("pw.forgot")}</Link>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full py-3.5" data-testid="auth-submit-btn">
            {busy ? <Spinner className="border-white/40 border-t-white" /> : isReg ? t("auth.register") : t("auth.login")}
          </button>
        </form>
        <div className="my-4 flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />{t("auth.or")}<div className="h-px flex-1 bg-slate-200" /></div>
        <GoogleButton testid="auth-google-btn" />
        <p className="mt-5 text-center text-sm text-slate-500">
          {isReg ? t("auth.haveAccount") : t("auth.noAccount")}{" "}
          <Link to={isReg ? "/login" : "/registro"} state={location.state} replace className="font-bold text-brand" data-testid="auth-switch-link">
            {isReg ? t("auth.login") : t("auth.register")}
          </Link>
        </p>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); navigate("/perfil", { replace: true }); })
      .catch(() => { setUser(false); navigate("/login", { replace: true }); });
  }, [navigate, setUser]);
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-white" data-testid="auth-callback">
      <Spinner />
      <p className="text-sm text-slate-500">{t("auth.signingIn")}</p>
    </div>
  );
}
